import React, { useState, useEffect } from 'react';
import { Download, Eye, EyeOff, User, X } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { useSeason, seasonLabel } from '../../SeasonContext';
import { usePlayerData } from './PlayerDataContext';
import { changePassword, updatePlayer } from '../../api';
import { NATIONALITIES, fmtWeight } from '../Players';
import NotificationsPanel from '../../NotificationsPanel';

const infoInputStyle = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
const infoLabelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' };

function EditMyInfoModal({ playerRecord, onSave, onClose }) {
  const [form, setForm] = useState({
    nationality: playerRecord?.nationality || '',
    dob: playerRecord?.dob ? playerRecord.dob.slice(0, 10) : '',
    height: playerRecord?.height || '',
    weight: playerRecord?.weight || '',
    dominant_foot: playerRecord?.dominant_foot || 'Right',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(form);
      onClose();
    } catch {
      setError('Failed to save. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 300, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', overflowY: 'auto' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>Edit My Information</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}><X size={18} /></button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={infoLabelStyle}>Nationality</label>
            <select style={infoInputStyle} value={form.nationality} onChange={e => set('nationality', e.target.value)}>
              <option value="">Select nationality</option>
              {NATIONALITIES.map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={infoLabelStyle}>Date of Birth</label>
            <input style={infoInputStyle} type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={infoLabelStyle}>Height (cm)</label>
              <input style={infoInputStyle} type="number" value={form.height} onChange={e => set('height', e.target.value)} placeholder="e.g. 182" />
            </div>
            <div>
              <label style={infoLabelStyle}>Weight (kg)</label>
              <input style={infoInputStyle} type="number" step="0.1" value={form.weight} onChange={e => set('weight', e.target.value)} placeholder="e.g. 78.4" />
            </div>
          </div>
          <div>
            <label style={infoLabelStyle}>Dominant Foot</label>
            <select style={infoInputStyle} value={form.dominant_foot} onChange={e => set('dominant_foot', e.target.value)}>
              <option>Right</option><option>Left</option><option>Both</option>
            </select>
          </div>
          {error && <div style={{ fontSize: 13, color: '#ef4444', fontWeight: 600 }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} style={{ padding: '10px 18px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: saving ? 'var(--border)' : '#2563eb', color: saving ? 'var(--text-dimmed)' : '#fff', cursor: saving ? 'default' : 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlayerSettings({ user }) {
  const { theme, setTheme } = useTheme();
  const { selectedSeasons, setSelectedSeasons, availableSeasons } = useSeason();
  const { playerRecord, refresh } = usePlayerData();
  const [editingInfo, setEditingInfo] = useState(false);
  const [promptReady, setPromptReady] = useState(!!window.deferredInstallPrompt);

  const handleSaveInfo = async (form) => {
    await updatePlayer(playerRecord.id, {
      ...playerRecord,
      nationality: form.nationality || null,
      dob: form.dob || null,
      height: form.height || null,
      weight: form.weight || null,
      dominant_foot: form.dominant_foot,
    });
    await refresh();
  };

  // Change password state
  const [cur,     setCur]     = useState('');
  const [nw,      setNw]      = useState('');
  const [conf,    setConf]    = useState('');
  const [showPw,  setShowPw]  = useState(false);
  const [pwMsg,   setPwMsg]   = useState(null);
  const [pwBusy,  setPwBusy]  = useState(false);

  const handleChangePassword = async () => {
    if (!cur || !nw || !conf) return setPwMsg({ ok: false, text: 'All fields are required.' });
    if (nw !== conf)          return setPwMsg({ ok: false, text: 'New passwords do not match.' });
    if (nw.length < 8)        return setPwMsg({ ok: false, text: 'Password must be at least 8 characters.' });
    setPwBusy(true); setPwMsg(null);
    try {
      await changePassword(user?.id, cur, nw);
      setPwMsg({ ok: true, text: 'Password updated successfully.' });
      setCur(''); setNw(''); setConf('');
    } catch (err) {
      const txt = err.message?.includes('401') || err.message?.includes('incorrect')
        ? 'Current password is incorrect.'
        : 'Failed to update password.';
      setPwMsg({ ok: false, text: txt });
    } finally { setPwBusy(false); }
  };

  useEffect(() => {
    if (window.deferredInstallPrompt) setPromptReady(true);
    const handler = () => setPromptReady(true);
    const onInstalled = () => { setPromptReady(false); window.deferredInstallPrompt = null; };
    window.addEventListener('installpromptready', handler);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('installpromptready', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!window.deferredInstallPrompt) return;
    window.deferredInstallPrompt.prompt();
    const { outcome } = await window.deferredInstallPrompt.userChoice;
    if (outcome === 'accepted') {
      window.deferredInstallPrompt = null;
      setPromptReady(false);
    }
  };

  const options = [
    { value: 'night', label: 'Night', icon: '🌙', desc: 'Dark background, easy on the eyes' },
    { value: 'day',   label: 'Day',   icon: '☀️', desc: 'Light background, high contrast'  },
  ];

  const toggleSeason = s => setSelectedSeasons([s]);

  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: 20 };

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', padding: 20 }}>
      <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 24px' }}>
        Settings
      </h2>

      {/* My Information */}
      {playerRecord && (
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <User size={14} /> My Information
            </div>
            <button onClick={() => setEditingInfo(true)} style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: '#60a5fa', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
              Edit
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
            Keep your personal details up to date.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              { label: 'Nationality', val: playerRecord.nationality },
              { label: 'Date of birth', val: playerRecord.dob ? new Date(playerRecord.dob).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : null },
              { label: 'Height', val: playerRecord.height ? `${playerRecord.height} cm` : null },
              { label: 'Weight', val: playerRecord.weight ? `${fmtWeight(playerRecord.weight)} kg` : null },
              { label: 'Dominant foot', val: playerRecord.dominant_foot },
            ].map(({ label, val }) => (
              <div key={label} style={{ background: 'var(--bg-app)', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: val ? 'var(--text-primary)' : 'var(--text-dimmed)' }}>{val || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editingInfo && (
        <EditMyInfoModal
          playerRecord={playerRecord}
          onSave={handleSaveInfo}
          onClose={() => setEditingInfo(false)}
        />
      )}

      {/* Season Filter */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Season Filter</div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
          Choose which season's data to display.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {availableSeasons.map(s => {
            const active = selectedSeasons.includes(s);
            return (
              <button key={s} onClick={() => toggleSeason(s)} style={{
                flex: 1, padding: '12px 10px', borderRadius: 12,
                border: `2px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
                background: active ? 'rgba(59,130,246,0.12)' : 'var(--bg-app)',
                color: active ? '#60a5fa' : 'var(--text-secondary)',
                fontWeight: 700, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit',
                textAlign: 'center',
              }}>
                {seasonLabel(s)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Change Password */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Change Password</div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>Update your login password.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            { val: cur, set: setCur, ph: 'Current password' },
            { val: nw,  set: setNw,  ph: 'New password (min 8 chars)' },
            { val: conf, set: setConf, ph: 'Confirm new password' },
          ].map(({ val, set, ph }) => (
            <div key={ph} style={{ position: 'relative' }}>
              <input type={showPw ? 'text' : 'password'} value={val} onChange={e => set(e.target.value)} placeholder={ph}
                style={{ width: '100%', padding: '12px 44px 12px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }} />
              <button type="button" onClick={() => setShowPw(s => !s)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2, display: 'flex', alignItems: 'center' }}>
                {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          ))}
          {pwMsg && <div style={{ fontSize: 13, color: pwMsg.ok ? '#10b981' : '#ef4444', fontWeight: 600 }}>{pwMsg.text}</div>}
          <button onClick={handleChangePassword} disabled={pwBusy}
            style={{ padding: '12px', borderRadius: 10, border: 'none', background: pwBusy ? 'var(--border)' : '#2563eb', color: pwBusy ? 'var(--text-dimmed)' : '#fff', fontSize: 14, fontWeight: 700, cursor: pwBusy ? 'default' : 'pointer', fontFamily: 'inherit', width: '100%' }}>
            {pwBusy ? 'Updating…' : 'Update Password'}
          </button>
        </div>
      </div>

      {/* Notifications */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>Notifications</div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
          Choose what you get notified about.
        </p>
        <NotificationsPanel user={user} />
      </div>

      {/* Install App */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Install App
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 16px' }}>
          Install HeraLabX on your device for quick access from the home screen.
        </p>
        <button
          onClick={handleInstall}
          disabled={!promptReady}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 18px', borderRadius: 10, border: 'none',
            background: promptReady ? '#2563eb' : 'var(--border)',
            color: promptReady ? '#fff' : 'var(--text-dimmed)',
            cursor: promptReady ? 'pointer' : 'default',
            fontSize: 14, fontWeight: 600, fontFamily: 'inherit', width: '100%',
            justifyContent: 'center',
          }}
        >
          <Download size={16} />
          Install HeraLabX
        </button>
      </div>

      {/* Appearance */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>
          Appearance
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 18px' }}>
          Choose how HeraLabX looks on your device.
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          {options.map(opt => {
            const active = theme === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => setTheme(opt.value)}
                style={{
                  flex: 1,
                  padding: '14px 12px',
                  borderRadius: 12,
                  border: `2px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
                  background: active ? 'rgba(59,130,246,0.12)' : 'var(--bg-app)',
                  color: active ? '#60a5fa' : 'var(--text-secondary)',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'center',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontSize: 26, marginBottom: 6 }}>{opt.icon}</div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{opt.label}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>{opt.desc}</div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
